import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS, PAGE_SIZE_DEFAULT } from "src/dm/utils/constants";
import {
  DealDetail,
  DealListItem,
  DealWriteRequest,
  PagedResult,
} from "src/dm/interfaces/Deal";

export interface DealsQueryParams {
  page?: number;
  pageSize?: number;
  status?: string;
  statuses?: string[];
  bannerCode?: string;
  categoryManagerCode?: number | null;
  dealTypeId?: number | null;
  vendorBrokerId?: number | null;
  ownerUserId?: string;
  description?: string;
  adStartFrom?: string;
  adStartTo?: string;
  promoStartFrom?: string;
  promoStartTo?: string;
  promoEndFrom?: string;
  promoEndTo?: string;
  dealId?: number | null;
  internalOnly?: boolean;
  termSheetOnly?: boolean;
  rewardOnly?: boolean;
  adPlanCode?: string;
  adStrength?: string;
  adBlockId?: number | null;
  hasScanback?: boolean;
  hasOffInvoice?: boolean;
  hasBillBack?: boolean;
  hasCountRecount?: boolean;
  hasPassThru?: boolean;
  dateColumn?: string;
  dateFrom?: string;
  dateTo?: string;
  apVendorNumber?: string;
  itemUpc?: number | null;
  itemCode?: number | null;
  caseUpc?: number | null;
  itemDescription?: string;
  familyCode?: number | null;
  groupCode?: number | null;
  subgroupCode?: number | null;
  itemVendorNumber?: number | null;
  // Legacy AdvancedSearch ddlConsumerMessage / ddlCoupon — single FK pickers
  // that EXISTS-join the DEAL_CONSUMER_MESSAGE / DEAL_COUPON child tables.
  consumerMessageTypeId?: number | null;
  couponTypeId?: number | null;
}

export const useDeals = (params: DealsQueryParams = {}) => {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? PAGE_SIZE_DEFAULT;

  return useQuery({
    queryKey: ["deals", page, pageSize, params],
    queryFn: async (): Promise<PagedResult<DealListItem>> => {
      const response = await apiClient.get<PagedResult<DealListItem>>(
        APIS.Deals,
        {
          params: {
            page,
            pageSize,
            status: params.status || undefined,
            statuses: params.statuses && params.statuses.length > 0 ? params.statuses : undefined,
            bannerCode: params.bannerCode || undefined,
            categoryManagerCode: params.categoryManagerCode ?? undefined,
            dealTypeId: params.dealTypeId ?? undefined,
            vendorBrokerId: params.vendorBrokerId ?? undefined,
            ownerUserId: params.ownerUserId || undefined,
            description: params.description || undefined,
            adStartFrom: params.adStartFrom || undefined,
            adStartTo: params.adStartTo || undefined,
            promoStartFrom: params.promoStartFrom || undefined,
            promoStartTo: params.promoStartTo || undefined,
            promoEndFrom: params.promoEndFrom || undefined,
            promoEndTo: params.promoEndTo || undefined,
            dealId: params.dealId ?? undefined,
            internalOnly: params.internalOnly || undefined,
            termSheetOnly: params.termSheetOnly || undefined,
            rewardOnly: params.rewardOnly || undefined,
            adPlanCode: params.adPlanCode || undefined,
            adStrength: params.adStrength || undefined,
            adBlockId: params.adBlockId ?? undefined,
            hasScanback: params.hasScanback || undefined,
            hasOffInvoice: params.hasOffInvoice || undefined,
            hasBillBack: params.hasBillBack || undefined,
            hasCountRecount: params.hasCountRecount || undefined,
            hasPassThru: params.hasPassThru || undefined,
            dateColumn: params.dateColumn || undefined,
            dateFrom: params.dateFrom || undefined,
            dateTo: params.dateTo || undefined,
            apVendorNumber: params.apVendorNumber || undefined,
            itemUpc: params.itemUpc ?? undefined,
            itemCode: params.itemCode ?? undefined,
            caseUpc: params.caseUpc ?? undefined,
            itemDescription: params.itemDescription || undefined,
            familyCode: params.familyCode ?? undefined,
            groupCode: params.groupCode ?? undefined,
            subgroupCode: params.subgroupCode ?? undefined,
            itemVendorNumber: params.itemVendorNumber ?? undefined,
            consumerMessageTypeId: params.consumerMessageTypeId ?? undefined,
            couponTypeId: params.couponTypeId ?? undefined,
          },
          paramsSerializer: {
            indexes: null, // statuses=A&statuses=B (not statuses[]=A)
          },
        }
      );
      return response.data;
    },
  });
};

export interface DealForbiddenError extends Error {
  isForbidden: true;
}

const isForbidden = (e: unknown): e is DealForbiddenError =>
  typeof e === "object" && e !== null && (e as { isForbidden?: boolean }).isForbidden === true;

export const isDealForbidden = isForbidden;

export const useDealDetail = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["deal", dealId],
    enabled: dealId !== undefined && dealId > 0,
    // Don't retry on a 403 — the visibility gate's decision won't change
    // by retrying. Retry transient network blips only.
    retry: (failureCount, error) => {
      if (isForbidden(error)) return false;
      return failureCount < 2;
    },
    queryFn: async (): Promise<DealDetail> => {
      try {
        const response = await apiClient.get<DealDetail>(
          `${APIS.Deals}/${dealId}`
        );
        return response.data;
      } catch (e) {
        // Surface the API's CheckForPermissions 403 as a typed error so
        // the layout can render a friendly "no permission" surface
        // instead of the generic error fallback (which would say "we hit
        // a problem"). Backend ref: DealAccessGuard.RequireCanEditDealAsync
        // + DealsController.Get visibility gate.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          const err = new Error(
            "You don't have permission to view this deal."
          ) as DealForbiddenError;
          err.isForbidden = true;
          throw err;
        }
        throw e;
      }
    },
  });

export const useCreateDeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DealWriteRequest): Promise<DealDetail> => {
      const response = await apiClient.post<DealDetail>(APIS.Deals, body);
      return response.data;
    },
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.setQueryData(["deal", deal.dealId], deal);
    },
  });
};

export const useUpdateDeal = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DealWriteRequest): Promise<DealDetail> => {
      const response = await apiClient.put<DealDetail>(
        `${APIS.Deals}/${dealId}`,
        body
      );
      return response.data;
    },
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.setQueryData(["deal", deal.dealId], deal);
    },
  });
};
